<template>
  <div>
    <b-row>
      <b-col md="12">
        <b-button
          variant="primary"
          @click="fetch"
        >
          <feather-icon
            icon="RefreshCwIcon"
            class="mr-1"
          />
          <span>Refresh</span>
        </b-button>
      </b-col>
      <b-col :md="currActiveEntity ? 3: 12">
        <b-tabs
          v-model="tabIndex"
          @input="clearActiveEntity"
        >
          <b-tab title="Categories">
            <b-row>
              <b-spinner
                v-if="fetchingCategories"
                variant="primary"
              />
              <template v-else-if="categories.length && !fetchingCategories">
                <b-col
                  v-for="category in categories"
                  :key="category.id"
                  :md="currActiveEntity ? 12 : 4"
                >
                  <entity-card
                    :entity="category"
                    @review="previewEntityHandler(category, CATEGORY)"
                  />
                </b-col>
              </template>
              <h6
                v-else-if="!fetchingCategories && !categories.length"
                class="ml-1"
              >
                No pending categories
              </h6>
            </b-row>
          </b-tab>
          <b-tab title="Items">
            <b-row>
              <b-spinner
                v-if="fetchingItems"
                variant="primary"
              />
              <template v-else-if="items.length && !fetchingItems">
                <b-col
                  v-for="item in items"
                  :key="item.id"
                  :md="currActiveEntity ? 12 : 4"
                >
                  <entity-card
                    :entity="item"
                    @review="previewEntityHandler(item, ITEM)"
                  />
                </b-col>
              </template>
              <h6
                v-else-if="!fetchingItems && !items.length"
                class="ml-1"
              >
                No pending items
              </h6>
            </b-row>
          </b-tab>
          <b-tab title="Groups">
            <b-row>
              <b-spinner
                v-if="fetchingGroups"
                variant="primary"
              />
              <template v-else-if="groups.length && !fetchingGroups">
                <b-col
                  v-for="group in groups"
                  :key="group.id"
                  :md="currActiveEntity ? 12 : 4"
                >
                  <entity-card
                    :entity="group"
                    @review="previewEntityHandler(group, GROUP)"
                  />
                </b-col>
              </template>
              <h6
                v-else-if="!fetchingGroups && !groups.length"
                class="ml-1"
              >
                No pending groups
              </h6>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-col>
      <b-col
        v-if="currActiveEntity"
        md="9"
      >
        <b-card style="margin-top:54px">
          <b-card-title class="d-flex justify-content-between">
            <h3>{{ currActiveEntityCopy.name.en }}</h3>
            <div>
              <b-button
                v-if="currActiveEntity.requestedChange"
                variant="warning"
                class="mr-1"
                @click="compareChangesHandler"
              >
                <span>Compare Changes</span>
                <feather-icon
                  v-if="compareChanges"
                  icon="XIcon"
                />
              </b-button>
              <b-button
                variant="primary"
                @click="clearActiveEntity"
              >
                <feather-icon
                  icon="XIcon"
                />
              </b-button>
            </div>
          </b-card-title>
          <b-row>
            <b-col :md="compareChanges ? 6 : 12">
              <b-badge
                v-if="compareChanges"
                class="mb-1"
                variant="danger"
              >
                Old Changes
              </b-badge>
              <CategoryDetails
                v-if="currActiveEntityType == CATEGORY"
                :category="currActiveEntity"
              />
              <ItemDetails
                v-if="currActiveEntityType == ITEM"
                :item="currActiveEntity"
              />
              <GroupDetails
                v-if="currActiveEntityType == GROUP"
                :group="currActiveEntity"
              />
            </b-col>
            <b-col
              v-if="compareChanges && currActiveEntity.requestedChange"
              md="6"
            >
              <b-badge
                class="mb-1"
                variant="success"
              >
                New Changes
              </b-badge>

              <CategoryDetails
                v-if="currActiveEntityType == CATEGORY"
                :category="currActiveEntity.requestedChange.changes"
              />
              <ItemDetails
                v-if="currActiveEntityType == ITEM"
                :item="currActiveEntity.requestedChange.changes"
              />
              <GroupDetails
                v-if="currActiveEntityType == GROUP"
                :group="currActiveEntity.requestedChange.changes"
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col
              md="12"
              class="mb-1"
            >
              <BFormTextarea
                v-model="rejectionReason"
                placeholder="Enter Rejection reasion"
              />
            </b-col>
            <b-col md="6">
              <b-button
                :disabled="applyingTransition"
                variant="primary"
                block
                @click="transitionEntity('APPROVE')"
              >
                <b-spinner
                  v-if="applyingTransition"
                  small
                />
                <span v-else>Approve</span>
              </b-button>
            </b-col>
            <b-col md="6">
              <b-button
                :disabled="!rejectionReason || applyingTransition"
                variant="danger"
                block
                @click="transitionEntity('REJECT')"
              >
                <b-spinner
                  v-if="applyingTransition"
                  small
                />
                <span v-else>Reject</span>
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTab, BTabs, BRow, BSpinner, BCol, BCard, BCardTitle, BButton, BFormTextarea, BBadge,
} from 'bootstrap-vue'
import { ref } from '@vue/composition-api'
import useRegisterStoreModule from '@/hooks/useRegisterStoreModule'
import businessStore from '../store'
import EntityCard from '../components/pending-approvals/EntityCard.vue'
import CategoryDetails from '../components/pending-approvals/CategoryDetails.vue'
import ItemDetails from '../components/pending-approvals/ItemDetails.vue'
import GroupDetails from '../components/pending-approvals/GroupDetails.vue'
import sleep from '@/utils/sleep'

const CATEGORY = 'CATEGORY'
const ITEM = 'ITEM'
const GROUP = 'GROUP'

export default {
  components: {
    BTab, BTabs, BRow, EntityCard, BSpinner, BCol, BCard, BCardTitle, BBadge, BButton, CategoryDetails, BFormTextarea, ItemDetails, GroupDetails,
  },
  setup(_, ctx) {
    useRegisterStoreModule('business', businessStore)
    const compareChanges = ref(false)
    const currActiveEntityCopy = ref()
    const currActiveEntityType = ref()
    const currActiveEntity = ref()
    const rejectionReason = ref('')
    const businessId = ctx.root.$route.params.id
    const tabIndex = ref(0)

    const categories = ref([])
    const fetchingCategories = ref(false)
    const fetchCategories = async () => {
      fetchingCategories.value = true
      const catsRes = await ctx.root.$store.dispatch('business/getAllCategories', {
        businessId,
        pendingReview: true,
      })
      const { data: categoriesList } = catsRes.data.data
      categories.value = categoriesList
      fetchingCategories.value = false
    }

    const items = ref([])
    const fetchingItems = ref(false)
    const fetchItems = async () => {
      fetchingItems.value = true
      const itemsRes = await ctx.root.$store.dispatch('business/getBusinessItems', {
        businessId,
        pendingReview: true,
      })
      const { data: itemsList } = itemsRes.data.data
      items.value = itemsList.items
      fetchingItems.value = false
    }

    const groups = ref([])
    const fetchingGroups = ref(false)
    const fetchGroups = async () => {
      fetchingGroups.value = true
      const groupsRes = await ctx.root.$store.dispatch('business/getBusinessSelectionGroups', {
        businessId,
        pendingReview: true,
      })
      const { data: groupsList } = groupsRes.data.data
      groups.value = groupsList
      fetchingGroups.value = false
    }

    const previewEntityHandler = (entity, entityType) => {
      currActiveEntityType.value = entityType
      currActiveEntity.value = { ...entity }
      currActiveEntityCopy.value = JSON.parse(JSON.stringify(currActiveEntity.value))
      rejectionReason.value = ''
    }
    const clearActiveEntity = () => {
      currActiveEntityType.value = undefined
      currActiveEntityCopy.value = undefined
      compareChanges.value = false
      currActiveEntity.value = undefined
      rejectionReason.value = ''
    }

    const applyingTransition = ref(false)
    const transitionEntity = async action => {
      applyingTransition.value = true
      try {
        if (currActiveEntityType.value === CATEGORY) {
          await ctx.root.$store.dispatch('business/transitionCategory', {
            category: currActiveEntity.value,
            body: {
              action,
              rejectionReason: action === 'REJECT' ? rejectionReason.value : undefined,
            },
          })
          await sleep()
          fetchCategories()
        } else if (currActiveEntityType.value === ITEM) {
          await ctx.root.$store.dispatch('business/transitionItem', {
            item: currActiveEntity.value,
            body: {
              action,
              rejectionReason: action === 'REJECT' ? rejectionReason.value : undefined,
            },
          })
          await sleep()
          fetchItems()
        } else if (currActiveEntityType.value === GROUP) {
          await ctx.root.$store.dispatch('business/transitionGroup', {
            group: currActiveEntity.value,
            body: {
              action,
              rejectionReason: action === 'REJECT' ? rejectionReason.value : undefined,
            },
          })
          await sleep()
          fetchGroups()
        }
        clearActiveEntity()
      } catch (err) {
        console.log(err)
      } finally {
        applyingTransition.value = false
      }
    }

    const compareChangesHandler = () => {
      compareChanges.value = !compareChanges.value
      if (compareChanges.value && currActiveEntity.value.requestedChange) {
        const requestedChangesTemp = currActiveEntity.value.requestedChange.changes
        currActiveEntity.value = {
          id: currActiveEntity.value.id,
          requestedChange: currActiveEntity.value.requestedChange,
          name: requestedChangesTemp.name ? currActiveEntity.value.name : undefined,
          description: requestedChangesTemp.description ? currActiveEntity.value.description : undefined,
          allergyNote: requestedChangesTemp.allergyNote ? currActiveEntity.value.allergyNote : undefined,
          imageUrl: requestedChangesTemp.imageUrl ? currActiveEntity.value.imageUrl : undefined,
          selections: requestedChangesTemp.selections ? currActiveEntity.value.selections : undefined,
          price: requestedChangesTemp.price ? currActiveEntity.value.price : undefined,
        }
      } else {
        currActiveEntity.value = JSON.parse(JSON.stringify(currActiveEntityCopy.value))
      }
    }
    const fetch = () => {
      fetchCategories()
      fetchItems()
      fetchGroups()
      clearActiveEntity()
    }
    fetch()

    return {
      previewEntityHandler,
      fetch,
      clearActiveEntity,
      currActiveEntity,
      applyingTransition,
      currActiveEntityType,
      compareChangesHandler,
      rejectionReason,
      tabIndex,
      items,
      groups,
      categories,
      fetchingCategories,
      fetchingItems,
      fetchingGroups,
      transitionEntity,
      CATEGORY,
      ITEM,
      currActiveEntityCopy,
      GROUP,
      compareChanges,
    }
  },
}
</script>

<style>

</style>
